import type { PRCPaidOtherContentDamage_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { type TableInformation } from '../../../config/flavourConfig';
import { type CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { type DogDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/pet';
import type {
    PrivateContentDTO,
    YouthContentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import { type CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';

export const getYouHaveInformedCoverableDetailsConfig = (
    coverable: CoverableDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation | undefined => {
    switch (coverable.flavourName) {
        case 'Hundeforsikring':
            return getDogYouHaveInformedCoverableDetailsConfig(coverable);
        case 'Indboforsikring til unge':
        case 'Indboforsikring':
            return getContentYouHaveInformedCoverableDetailsConfig(coverable);
        case 'Bilforsikring':
            return getPrivateCarYouHaveInformedCoverableDetailsConfig(
                coverable as CarDTO,
                getHippoText
            );
        default:
            return undefined;
    }
};

const getDogYouHaveInformedCoverableDetailsConfig = (dogCoverable: DogDTO): TableInformation => {
    return {
        title: 'Du har oplyst',
        rowsInformation: [
            {
                name: 'healthyDog',
                fieldValue:
                    'Hunden har været sund og rask og uden sygdomme eller skader de seneste 6 måneder, inden denne hundeforsikring blev købt',
                hidden: !dogCoverable.isHealthyFor6Months
            }
        ]
    };
};

const getOtherClaimsInPast2YearsFieldValue = (
    otherClaims: PRCPaidOtherContentDamage_Top | undefined
): string => {
    if (!otherClaims) return '';

    if (otherClaims === 'No') {
        return `Der inden for de sidste 2 år før købet af denne forsikring ikke er blevet erstattet skader på en indboforsikring, \
som du har været omfattet af`;
    }

    if (/^1Claim/.test(otherClaims)) {
        return `Der inden for de sidste 2 år før købet af denne forsikring er blevet erstattet 1 skade på en indboforsikring, \
som du har været omfattet af`;
    }

    return `Der inden for de sidste 2 år før købet af denne forsikring er blevet erstattet 2 skade på en indboforsikring, \
som du har været omfattet af`;
};

const getContentYouHaveInformedCoverableDetailsConfig = (
    coverable: YouthContentDTO | PrivateContentDTO
): TableInformation => {
    const otherClaimsInPast2YearsFieldValue = getOtherClaimsInPast2YearsFieldValue(
        coverable.otherClaimsInPast2Years
    );

    return {
        title: 'Du har oplyst',
        rowsInformation: [
            {
                name: 'Not covered by a content insurance today',
                fieldValue:
                    'Du på tidspunktet for købet af denne forsikring ikke var omfattet af en anden indboforsikring',
                hidden:
                    !coverable.hasContentInsurance?.includes('No') ||
                    coverable.hasContentInsurance === 'YesNormal' ||
                    coverable.hasContentInsurance === 'YesNoticeGiven'
            },
            {
                name: 'Is covered by a content insurance today',
                fieldValue:
                    'Du på tidspunktet for købet af denne forsikring var omfattet af en anden indboforsikring',
                hidden: !coverable.hasContentInsurance?.includes('Yes')
            },
            {
                name: 'Has cloudburst claims 5 years',
                fieldValue: `Der inden for de sidste 5 år før købet af denne forsikring er blevet erstattet skader som følge af sky- eller \
tøbrud på denne adresse på en indboforsikring, som du har været omfattet af`,
                hidden: coverable.hasContentCloudBurstClaim5Yrs?.includes('No')
            },
            {
                name: 'No cloudburst claims 5 years',
                fieldValue: `Der inden for de sidste 5 år før købet af denne forsikring ikke er blevet erstattet skader som følge af sky- eller \
tøbrud på denne adresse på en indboforsikring, som du har været omfattet af`,
                hidden: !coverable.hasContentCloudBurstClaim5Yrs?.includes('No')
            },
            {
                name: 'Other claims in the last 2 years',
                fieldValue: otherClaimsInPast2YearsFieldValue,
                hidden: !otherClaimsInPast2YearsFieldValue
            }
        ]
    };
};

const getPrivateCarYouHaveInformedCoverableDetailsConfig = (
    coverable: CarDTO,
    getHippoText: (resourceKey: string) => string
): TableInformation => {
    return {
        title: 'Du har oplyst',
        rowsInformation: [
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-CARS-PRIMARY-USER-SENIORITY-TITLE'),
                fieldValue: coverable.drivingExperience
            },
            {
                name: getHippoText('DSS-COVERABLE-DETAILS-NUMBER-OF-CLAIMS-PAST-THREE-YEARS-TITLE'),
                fieldValue: coverable.numberOfClaims.toString()
            }
        ]
    };
};
